/*
 * System announcements as one full-width strip above the app (sidebar and
 * tab bar both): every active announcement, most severe first, one at a
 * time with "1 of N" arrows. Dismissal is per user and per announcement;
 * resolved announcements are never fetched. Announcements appear nowhere
 * else in the app.
 */
import { AlertTriangle, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useState, type FC } from "react";

import { AgentPeek } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";
import { cn } from "@/lib/utils";

import {
  CLOSED_UPDATES_EVENT,
  closeUpdate,
  readClosedUpdateIds
} from "../../helpers/closedCriticalUpdates";
import useAdminUpdates from "../../hooks/admin/useManageAdminUpdates";
import type { AdminUpdate } from "../../pages/Admin/Updates/AdminUpdate";

const SEVERITY: Record<AdminUpdate["priority"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3
};

type Look = {
  strip: string;
  icon: string;
  /** Description and pager. */
  muted: string;
  /** Pager and dismiss buttons; empty keeps the ghost button's colors. */
  control: string;
};

const LOOK: Record<AdminUpdate["priority"], Look> = {
  critical: {
    strip: "bg-destructive/5 text-destructive",
    icon: "text-destructive",
    muted: "text-muted-foreground",
    control: ""
  },
  high: {
    strip: "bg-warning-50 text-foreground",
    icon: "text-warning-700",
    muted: "text-muted-foreground",
    control: ""
  },
  medium: {
    strip: "bg-warning-50 text-foreground",
    icon: "text-warning-700",
    muted: "text-muted-foreground",
    control: ""
  },
  // Inverted ink: black with white text in light mode, light in dark mode.
  low: {
    strip: "bg-fg text-chrome",
    icon: "",
    muted: "text-chrome/70",
    control: "text-chrome/70 hover:bg-chrome/15 hover:text-chrome"
  }
};

export const STRIP_HEIGHT_VAR = "--app-strip-h";
const STRIP_HEIGHT = "30px";

const updatedMs = (u: AdminUpdate): number =>
  (u.updatedAt ?? u.createdAt)?.toMillis?.() ?? 0;

/** Active, not dismissed, most severe first, then newest. */
export const visibleAnnouncements = (
  updates: AdminUpdate[],
  closed: string[]
): AdminUpdate[] =>
  updates
    .filter((u) => u.status === "active" && !!u.id && !closed.includes(u.id))
    .sort(
      (a, b) =>
        SEVERITY[a.priority] - SEVERITY[b.priority] ||
        updatedMs(b) - updatedMs(a)
    );

export const AnnouncementStrip: FC = () => {
  const {
    userDetails: { id: uid }
  } = useGetUserDetails();
  const { adminUpdates, fetchAdminUpdates } = useAdminUpdates();
  const [closed, setClosed] = useState<string[]>(() =>
    readClosedUpdateIds(uid)
  );
  const [at, setAt] = useState(0);

  useEffect(() => {
    void fetchAdminUpdates();
    // Fetch once per mount.
  }, []);

  useEffect(() => {
    const sync = () => setClosed(readClosedUpdateIds(uid));
    sync();
    window.addEventListener(CLOSED_UPDATES_EVENT, sync);
    return () => window.removeEventListener(CLOSED_UPDATES_EVENT, sync);
  }, [uid]);

  const list = useMemo(
    () => visibleAnnouncements(adminUpdates, closed),
    [adminUpdates, closed]
  );
  const index = Math.min(at, Math.max(0, list.length - 1));
  const current = list[index];
  const showing = !!current?.id;

  // The sidebar is fixed to the viewport; it starts below the strip.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (showing) root.style.setProperty(STRIP_HEIGHT_VAR, STRIP_HEIGHT);
    else root.style.removeProperty(STRIP_HEIGHT_VAR);
    return () => {
      root.style.removeProperty(STRIP_HEIGHT_VAR);
    };
  }, [showing]);

  if (!current?.id) return null;

  const look = LOOK[current.priority] ?? LOOK.low;
  const paged = list.length > 1;
  // The ghost variant recolors icons that carry no text- class of their own.
  const glyph = look.control ? "text-current" : undefined;

  return (
    <div
      role="status"
      data-testid="announcement-strip"
      data-priority={current.priority}
      className={cn(
        "relative flex h-7.5 shrink-0 items-center justify-center text-xs",
        paged ? "px-32" : "px-10",
        look.strip
      )}
    >
      <div className="flex min-w-0 items-center justify-center gap-2 self-stretch">
        {current.priority === "low" ? (
          <span className="agent-peek-stage block shrink-0 self-end">
            <AgentPeek className="h-6" />
          </span>
        ) : (
          <AlertTriangle className={cn("size-3.5 shrink-0", look.icon)} />
        )}
        <span className="shrink-0 font-semibold">{current.title}</span>
        <span className={cn("min-w-0 truncate", look.muted)}>
          {current.description}
        </span>
      </div>
      <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
        {paged ? (
          <>
            <Button
              variant="ghost"
              size="icon-xs"
              className={cn("size-6", look.control)}
              aria-label="Previous announcement"
              disabled={index === 0}
              onClick={() => setAt(index - 1)}
            >
              <ChevronLeft className={cn("size-3", glyph)} />
            </Button>
            <span
              className={cn("tabular-nums", look.muted)}
              data-testid="announcement-pager"
            >
              {index + 1} of {list.length}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              className={cn("size-6", look.control)}
              aria-label="Next announcement"
              disabled={index === list.length - 1}
              onClick={() => setAt(index + 1)}
            >
              <ChevronRight className={cn("size-3", glyph)} />
            </Button>
          </>
        ) : null}
        <Button
          variant="ghost"
          size="icon-xs"
          className={cn("size-6 shrink-0", look.control || look.muted)}
          aria-label="Dismiss announcement"
          onClick={() => closeUpdate(current.id as string, uid)}
        >
          <X className={cn("size-3.5", glyph)} />
        </Button>
      </div>
    </div>
  );
};
